import React from 'react';
import { ValuesCache } from '@leverege/value-cache';
import turfCenter from '@turf/center';
import { GeoJsonLayer } from '@deck.gl/layers';
import { PathStyleExtension } from '@deck.gl/extensions';
import DeckGLLayer from "./layer/DeckGLLayer.js"; // import IconClusterLayer from './layer/IconClusterLayer.js'
const ensure = (obj, style, layerGroup) => {
  if (obj[style] == null) {
    obj[style] = {
      [layerGroup]: []
    };
  } else if (obj[style][layerGroup] == null) {
    obj[style][layerGroup] = [];
  }
};
const createLayerGroups = () => {
  return {
    fillFeatures: {},
    labelFeatures: {},
    symbolFeatures: {},
    lineFeatures: {},
    polyFeatures: {},
    outlineFeatures: {}
  };
};
export default class DeckGLLayerCreator {
  constructor(opts) {
    const options = opts || {};
    this.dataType = options.dataType || 'geoshapes';
    this.dataRef = options.dataRef;
    this.adapter = options.adapter;
    this.cache = new ValuesCache();
    this.addToNormal = options.addToNormal == null ? true : options.addToNormal;
    const id = options.id || 'gs';
    this.fKey = `${id}-fill`;
    this.oKey = `${id}-outline`;
    this.sKey = `${id}-symbol`;
    this.lKey = `${id}-label`;
  }
  getLayers(items, options) {
    if (this.cache.isSame('layers', items, options)) {
      return this.cache.value('layers');
    }
    const layerGroups = createLayerGroups();
    this.adapter.iterate(items, options, (shape, id) => {
      const sData = this.adapter.getShapeData(shape, id, options);
      this.addShapeDataToLayers(sData, id, layerGroups);
    });
    const oArr = this.layerGroupsToFeatureLayers(layerGroups, options);
    return this.cache.set('layers', oArr, items, options);
  }
  layerGroupsToFeatureLayers(layerGroups, options) {
    const {
      fillFeatures,
      outlineFeatures,
      labelFeatures,
      symbolFeatures
    } = layerGroups;
    const {
      index
    } = options;
    const oArr = [];
    Object.keys(labelFeatures).forEach(style => {
      Object.keys(labelFeatures[style]).forEach(layerGroup => {
        const id = this.createId(this.lKey, style, layerGroup);
        const labelStyle = this.adapter.getLabelLayerOptions?.(style, layerGroup, options);
        oArr.push(/*#__PURE__*/React.createElement(DeckGLLayer, {
          append: layerGroup !== 'default',
          key: id,
          index: index,
          layer: new GeoJsonLayer({
            ...labelStyle,
            pointType: 'text',
            id,
            data: labelFeatures[style][layerGroup]
          })
        }));
      });
    });
    Object.keys(symbolFeatures).forEach(style => {
      Object.keys(symbolFeatures[style]).forEach(layerGroup => {
        const id = this.createId(this.sKey, style, layerGroup);
        const symbolStyle = this.adapter.getSymbolLayerOptions?.(style, layerGroup, options);
        oArr.push(/*#__PURE__*/React.createElement(DeckGLLayer, {
          append: layerGroup !== 'default',
          key: id,
          index: index,
          layer: new GeoJsonLayer({
            ...symbolStyle,
            pointType: symbolStyle.pointType ? symbolStyle.pointType : symbolStyle.getIcon ? 'icon' : 'circle',
            id,
            data: symbolFeatures[style][layerGroup],
            extensions: [new PathStyleExtension({
              dash: true
            })]
          })
        }));
      });
    });
    Object.keys(fillFeatures).forEach(style => {
      Object.keys(fillFeatures[style]).forEach(layerGroup => {
        const id = this.createId(this.fKey, style, layerGroup);
        const fillStyle = this.adapter.getFillLayerOptions?.(style, layerGroup, options);
        oArr.push(/*#__PURE__*/React.createElement(DeckGLLayer, {
          append: layerGroup !== 'default',
          key: id,
          index: index,
          layer: new GeoJsonLayer({
            ...fillStyle,
            id,
            data: fillFeatures[style][layerGroup],
            extensions: [new PathStyleExtension({
              dash: true
            })]
          })
        }));
      });
    });
    Object.keys(outlineFeatures).forEach(style => {
      Object.keys(outlineFeatures[style]).forEach(layerGroup => {
        const id = this.createId(this.oKey, style, layerGroup);
        const fillStyle = this.adapter.getOutlineLayerOptions?.(style, layerGroup, options);
        oArr.push(/*#__PURE__*/React.createElement(DeckGLLayer, {
          append: layerGroup !== 'default',
          key: id,
          index: index,
          layer: new GeoJsonLayer({
            ...fillStyle,
            id,
            data: outlineFeatures[style][layerGroup],
            extensions: [new PathStyleExtension({
              dash: true
            })]
          })
        }));
      });
    });
    return oArr;
  }
  createId(base, style, layerGroup) {
    if (style === 'default') {
      return layerGroup === 'default' ? base : `${base}-${layerGroup}`;
    }
    return layerGroup === 'default' ? `${base}-${style}` : `${base}-${layerGroup}-${style}`;
  }
  addShapeDataToLayers(shapeData, id, {
    fillFeatures,
    outlineFeatures,
    symbolFeatures,
    labelFeatures
  }) {
    if (shapeData == null) {
      return;
    }
    const sData = Array.isArray(shapeData) ? shapeData : [shapeData];
    for (let i = 0; i < sData.length; i++) {
      const {
        dataType,
        dataRef,
        geoJson,
        appearance: app,
        labelCenter
      } = sData[i];
      if (app.visible === false) {
        continue;
      }
      const layerGroup = app.layerGroup || 'default';
      const {
        showFill = true,
        fillStyle = 'default',
        showOutline = true,
        outlineStyle = 'default',
        showSymbol = false,
        symbolStyle = 'default',
        showLabel = true,
        labelStyle = 'default'
      } = app;
      const geo = geoJson && geoJson.geometry;
      if (geo == null || geo.coordinates == null) {
        continue; // no coordinates to draw
      }
      const p = {
        dataType: dataType || this.dataType,
        dataRef: dataRef || this.dataRef,
        dataId: id,
        id,
        ...app
      };
      const feat = {
        id,
        type: 'Feature',
        properties: p,
        geometry: geo
      };
      if (showFill && geo.type === 'Polygon') {
        ensure(fillFeatures, fillStyle, layerGroup);
        if (layerGroup !== 'default') {
          if (this.addToNormal) {
            ensure(fillFeatures, fillStyle, 'default');
            fillFeatures[fillStyle].default.push(feat);
          } else {
            fillFeatures[fillStyle][layerGroup].push(feat);
          }
        } else {
          fillFeatures[fillStyle][layerGroup].push(feat);
        }
      }
      if (showOutline && geo.type === 'LineString') {
        ensure(outlineFeatures, outlineStyle, layerGroup);
        // ToDo: Need to loop over all polygon's arrays
        if (layerGroup !== 'default') {
          if (this.addToNormal) {
            ensure(outlineFeatures, outlineStyle, 'default');
            outlineFeatures[outlineStyle]['default'].push(feat); // eslint-disable-line  
          } else {
            outlineFeatures[outlineStyle][layerGroup].push(feat);
          }
        } else {
          outlineFeatures[outlineStyle][layerGroup].push(feat);
        }
      }
      if (showSymbol) {
        ensure(symbolFeatures, symbolStyle, layerGroup);
        if (layerGroup !== 'default') {
          if (this.addToNormal) {
            ensure(symbolFeatures, symbolStyle, 'default');
            symbolFeatures[symbolStyle]['default'].push(feat); // eslint-disable-line  
          } else {
            symbolFeatures[symbolStyle][layerGroup].push(feat);
          }
        } else {
          symbolFeatures[symbolStyle][layerGroup].push(feat);
        }
      }
      if (showLabel) {
        ensure(labelFeatures, labelStyle, layerGroup);
        const cent = labelCenter || turfCenter(geoJson);
        let pt = cent.geometry ? cent.geometry.coordinates : Array.isArray(cent) ? cent : [cent.lon, cent.lat];
        pt = pt.length === 2 ? [...pt, 20] : pt;
        const lFeat = {
          ...feat,
          geometry: {
            ...feat.geometry,
            type: 'Point',
            coordinates: pt
          }
        };
        labelFeatures[labelStyle][layerGroup].push(lFeat);
      }
    }
  }
}